import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, FormControl, Validators } from '@angular/forms';
import { toNumber } from 'lodash';
import { Observable, Subscription, combineLatest, of } from 'rxjs';
import { debounceTime, map, switchMap } from 'rxjs/operators';
import { MissingSideMembersApiService } from 'src/api/services/missing-side-members-api.service';
import { CalsiumListConfig, ColumnDataType } from 'src/app/calsiumlist/calsiumlist.component.model';
import { ErrorHandlerService } from 'src/app/errorhandler/error-handler.service';
import { ErrorTypeEnum } from 'src/app/shared/enums.model';
import { CalsiumDateFormatter } from 'src/app/shared/calsium-date-formatter';
import { MasterDataStateService, Paging, ScaniaUnit } from 'src/state';
import { CommonStateService } from 'src/state/common/service';
import { MissingSideMembersStateService } from 'src/state/missing-side-members/service';
import { MissingSideMembers } from 'src/state/missing-side-members/state-models/missing-side-members';
import { environment } from 'src/environments/environment';

@Component({
    selector: 'cal-missing-side-members',
    templateUrl: './missing-side-members.component.html',
    styleUrl: './missing-side-members.component.css',
    standalone: false
})
export class MissingSideMembersComponent implements OnInit {
  selectedScaniaUnitId: number = 0;
  allScaniaUnitIds: number[] = [];
  resources: any = this.commonService.getResources();
  searchComponentsForm: UntypedFormGroup;
  scaniaUnitList: Observable<ScaniaUnit[]>;
  fileName: string = '';
  showData: boolean = false;
  endDateMax: string;
  endDateMin: string;
  daysLimit: number = 35;

  dataList: Observable<MissingSideMembers[]>;
  listConfig: CalsiumListConfig;
  paging: Observable<Paging>;
  totalCount: Observable<number>;
  subscriptions = new Subscription();
  isLoading: Observable<boolean>;
  sortingExpression: Observable<string>;
  filterExpression: Observable<string>;


  constructor(
    private missingSideMembersApiService: MissingSideMembersApiService,
    private commonService: CommonStateService,
    private masterDataService: MasterDataStateService,
    private errHandler: ErrorHandlerService,
    private formBuilder: UntypedFormBuilder,
    private missingSideMembersService: MissingSideMembersStateService
  ) { }

  ngOnInit() {
    this.buildForm();
    this.fillSearchForm();
    this.scaniaUnitList = this.masterDataService.getScaniaUnitsByTypes(['Final Assembly', 'Allocation']).pipe(
      map(list => { 
        const filtered = list.filter(x => environment.missingSideMembersDB.includes(x.name));
        this.allScaniaUnitIds = filtered.map(x => Number(x.id));
        return filtered;
      }
    ));
    this.isLoading = this.commonService.getLoading();

    this.sortingExpression = this.missingSideMembersService.getSortingExpression();
    this.filterExpression = this.missingSideMembersService.getFilterExpression();
    this.totalCount = this.missingSideMembersService.getTotalCount();
    this.paging = this.missingSideMembersService.getPaging();
    this.dataList = this.missingSideMembersService.getList();

    let pageInitialLoad = true;
    this.subscriptions.add(
      combineLatest(this.paging, this.sortingExpression, this.filterExpression).pipe(
        debounceTime(0),
        switchMap(([paging, sortingExpression, filterExpression]) => {
          if (!pageInitialLoad) {
            return this.missingSideMembersService.loadMissingSideMembers(
              this.getScaniaUnitIds(),
              this.searchComponentsForm.value.startDate,
              this.searchComponentsForm.value.endDate,
              paging,
              sortingExpression,
              filterExpression
            );
          }
          pageInitialLoad = false;
          return new Promise<void>(null);
        })
      ).subscribe()
    );
    this.buildList();
  }

  ngOnDestroy() { this.subscriptions.unsubscribe(); }

  buildForm() {
    this.searchComponentsForm = this.formBuilder.group({
      selectedScaniaUnit: ["", Validators.required]
    });
  }

  fillSearchForm(fromDate: Date = null, toDate: Date = null, scaniaUnit: string = '') {

    this.searchComponentsForm = this.formBuilder.group({
      startDate: [CalsiumDateFormatter.getDateOrDefault(fromDate, true), []],
      endDate: [CalsiumDateFormatter.calculateToDateWithLimit(fromDate, this.daysLimit), []],
      selectedScaniaUnit: [scaniaUnit, <any>Validators.required]
    });

    this.endDateMin = CalsiumDateFormatter.getDateOrDefault(fromDate, true);
    this.endDateMax = CalsiumDateFormatter.calculateToDateWithLimit(fromDate, this.daysLimit);
    this.searchComponentsForm.get('startDate')?.valueChanges
      .subscribe((newStartDateString: string) => {
        const newStartDate: Date = new Date(newStartDateString);
        const newEndDate = CalsiumDateFormatter.calculateToDateWithLimit(newStartDate, this.daysLimit);
        this.searchComponentsForm.patchValue({
          endDate: newEndDate,
        });
        this.endDateMin = newStartDateString;
        this.endDateMax = newEndDate;
      });

  }

  getScaniaUnitIds(): number[]{
    const selected = this.searchComponentsForm?.value?.selectedScaniaUnit ?? this.selectedScaniaUnitId;
    const selectedId = Number(selected);
    return (selectedId === 0) ? this.allScaniaUnitIds.slice() : [selectedId]
  }

  onExportToExcel() {
      if (!this.isValidForm()) return;
      const fromDate = this.searchComponentsForm.value.startDate;
      const toDate = this.searchComponentsForm.value.endDate;
      const scaniaUnitId = this.getScaniaUnitIds();
      if (scaniaUnitId.length === this.allScaniaUnitIds.length) {
        this.fileName = `All_${fromDate}_${toDate}_missingSideMembers`;
      }
      else {
        const scaniaUnit = this.masterDataService.getState().scaniaUnits.find(su => Number(su.id) === Number(scaniaUnitId[0]));
        this.fileName = `${scaniaUnit.name}_${fromDate}_${toDate}_missingSideMembers`;
      }
    
    const payload = {
      from: new Date(fromDate).toISOString(),
      to: new Date(toDate).toISOString(),
      allocationScaniaUnit: scaniaUnitId,
    };
    return this.missingSideMembersApiService.exportMissingSideMembers(payload, this.fileName);
  }

  buildList() {
    this.listConfig = new CalsiumListConfig();
    this.listConfig.ListCaption = this.resources.List;
    this.listConfig.ShowFilterRow = true;
    this.listConfig.ParentComponent = this;
    this.listConfig.ShowExportToExcel = false;

    let column;

    column = this.listConfig.AddListColumn(ColumnDataType.string, 'chassisNumber', this.resources.ChassisNumber);
    column.AllowFilter = true;
    column.Sortable = true;

    column = this.listConfig.AddListColumn(ColumnDataType.string, 'productIndividualNumber', this.resources.ProductIndividualNumber);
    column.AllowFilter = true;
    column.Sortable = true;

    column = this.listConfig.AddListColumn(ColumnDataType.string, 'popId', this.resources.PopId);
    column.AllowFilter = true;
    column.Sortable = true;

    column = this.listConfig.AddListColumn(ColumnDataType.date, 'productionDate', this.resources.ProductionDate);
    column.AllowFilter = true;
    column.Sortable = true;

    column = this.listConfig.AddListColumn(ColumnDataType.date, 'invoiceDate', this.resources.InvoiceDate);
    column.AllowFilter = true;
    column.Sortable = true;

    column = this.listConfig.AddListColumn(ColumnDataType.string, 'description', this.resources.Description);
    column.AllowFilter = true;
    column.Sortable = true;
  }

  onPaging(page: number) {
    this.missingSideMembersService.goToPage(page);
  }

  onSort(sortExpression: string) {
    this.missingSideMembersService.sort(sortExpression);
  }

  onFilter(filterExpression: string) {
    this.missingSideMembersService.filter(filterExpression);
  }

  onClearFilterAndSorting() {
    this.missingSideMembersService.clearFilterAndSorting();
  }
  onSearch(): any {
    if (this.isValidForm()) {
      this.showData = true;
      const fromDate = this.searchComponentsForm.value.startDate;
      const toDate = this.searchComponentsForm.value.endDate;
      const scaniaUnitId = this.getScaniaUnitIds();
      // console.log('scaniaUnitIds',scaniaUnitId);

      return this.missingSideMembersService.refetchList(
        scaniaUnitId,
        fromDate,
        toDate);
    }
  }
  onUpdate(): any {
    if (this.isValidForm()) {
      this.showData = true;
      const fromDate = this.searchComponentsForm.value.startDate;
      const toDate = this.searchComponentsForm.value.endDate;
      const scaniaUnitId = this.getScaniaUnitIds();

       this.missingSideMembersService.update(
        scaniaUnitId,
        fromDate,
         toDate).then((data) => {
           if (data) {
             this.errHandler.emitError(this.resources.SideMemberUpdate, this.resources.Success, ErrorTypeEnum.Success);
           } else {         
             this.errHandler.emitError(this.resources.SideMemberUpdateError, this.resources.Error, ErrorTypeEnum.Error);
           }
         });
      
      return "";
    }
  }


  isValidForm(): boolean {
    let valid = true;
    const selectedUnit = this.searchComponentsForm.get('selectedScaniaUnit');

    if (selectedUnit?.errors?.required) {
      this.errHandler.emitError(
        this.resources.ScaniaUnitIsRequired,
        this.resources.RequiredFieldError,
        ErrorTypeEnum.Error
      );
      valid = false;
    }
    return valid;
  }

  reset(): void {
    this.searchComponentsForm.reset();
  }


}

